import { sectionRenderer, morphSection, buildSectionSelector } from '@theme/section-renderer';
import { FilterUpdateEvent } from '@theme/events';
import { startViewTransition } from '@theme/utilities';

/**
 * Progressive-enhancement wrapper around the collection-header nav.
 *
 * Selecting an item swaps the product grid over to that collection in place, so
 * the shopper keeps the page they are on (heading, breadcrumbs and the header
 * list itself) and only the underline moves. Selecting the underlined item
 * again clears it, back to the whole collection.
 *
 * Every item is a real <a> to the collection, so with no JS — or if the swap
 * fails — it behaves as a normal link.
 */
class FvCollectionHeader extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    window.addEventListener('popstate', this.#syncActive);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    window.removeEventListener('popstate', this.#syncActive);
  }

  /** @returns {string} The collection this header belongs to. */
  get baseUrl() {
    return this.dataset.baseUrl || window.location.pathname;
  }

  /** @returns {HTMLAnchorElement[]} */
  get links() {
    return [.../** @type {NodeListOf<HTMLAnchorElement>} */ (this.querySelectorAll('a[data-fv-header]'))];
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('a[data-fv-header]');
    if (!(link instanceof HTMLAnchorElement)) return;

    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();

    // Clicking the selected item clears it, back to the whole collection.
    const isActive = link.classList.contains('is-active');
    this.#select(isActive ? this.baseUrl : link.href);
  };

  /**
   * @param {string} destination - The collection to show.
   */
  async #select(destination) {
    const url = this.#buildUrl(destination);
    const sectionId = document.querySelector('results-list')?.getAttribute('section-id');

    if (!sectionId) {
      window.location.assign(url.toString());
      return;
    }

    let html;

    try {
      html = await sectionRenderer.getSectionHTML(sectionId, true, new URL(url));

      // A collection on a different template won't contain this section.
      const fragment = new DOMParser().parseFromString(html, 'text/html');
      if (!fragment.getElementById(buildSectionSelector(sectionId))) throw new Error('Grid section missing');
    } catch {
      window.location.assign(url.toString());
      return;
    }

    history.pushState({ fvHeader: true }, '', url.toString());
    this.#syncActive();

    // Clears the grid's cached pages and re-renders the toolbar (its filters and
    // size chips belong to the collection being shown).
    document.dispatchEvent(new FilterUpdateEvent(url.searchParams));

    startViewTransition(() => morphSection(sectionId, html), ['product-grid']);
  }

  /**
   * Carries the shopper's facets and sort across to the selected collection.
   * The in-stock default is applied here too, since layout/theme.liquid only
   * gets to add it on a page load and there isn't one.
   * @param {string} destination
   * @returns {URL}
   */
  #buildUrl(destination) {
    const url = new URL(destination, window.location.origin);

    for (const [key, value] of new URL(window.location.href).searchParams) {
      if (key.startsWith('filter.')) url.searchParams.append(key, value);
      if (key === 'sort_by') url.searchParams.set(key, value);
    }

    if (!url.searchParams.has('filter.v.availability')) {
      url.searchParams.set('filter.v.availability', '1');
    }

    return url;
  }

  /** Underlines the item matching the collection currently on show. */
  #syncActive = () => {
    for (const link of this.links) {
      const isActive = new URL(link.href).pathname === window.location.pathname;

      link.classList.toggle('is-active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  };
}

if (!customElements.get('fv-collection-header')) {
  customElements.define('fv-collection-header', FvCollectionHeader);
}
